/**
 * Service for managing multiple user sessions in localStorage
 * Stores complete OIDC user objects to enable seamless switching between accounts
 */

import { getActiveOidcStorageKey, getConfiguredOidcClientConfig } from '../config/oidc-config';

const STORAGE_KEY = 'approvn_user_sessions';
const MAX_STORED_USERS = 5; // Maximum number of users to remember

export interface StoredUserSession {
  id: string;
  username: string;
  email: string;
  name: string;
  given_name?: string;
  family_name?: string;
  lastLoginAt: string;
  // Store the complete OIDC user object for seamless switching
  oidcUser: any;
}

export class UserSessionStorage {
  /**
   * Get all stored user sessions
   */
  static getSessions(): StoredUserSession[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const sessions = JSON.parse(stored) as StoredUserSession[];
      
      // Filter out sessions without OIDC data (from before OIDC support was added)
      // This ensures we only keep sessions that can actually be used for switching
      return sessions.filter(s => {
        if (!s.oidcUser || !s.oidcUser.access_token) {
          console.log(`[UserSessionStorage] Filtering out session without OIDC data: ${s.username}`);
          return false;
        }
        return true;
      });
    } catch (error) {
      console.error('Failed to load user sessions:', error);
      return [];
    }
  }

  /**
   * Save or update a user session
   */
  static saveSession(userInfo: Omit<StoredUserSession, 'lastLoginAt'>): void {
    try {
      // Validate that we have OIDC data before saving
      if (!userInfo.oidcUser || !userInfo.oidcUser.access_token) {
        console.warn('[UserSessionStorage] Skipping save - no OIDC data provided for:', userInfo.username);
        return;
      }
      
      const sessions = this.getSessions();
      
      // Remove existing session for this user if present
      const filteredSessions = sessions.filter(s => s.username !== userInfo.username);
      
      // Add the new/updated session at the beginning
      const newSession: StoredUserSession = {
        ...userInfo,
        lastLoginAt: new Date().toISOString(),
      };
      
      const updatedSessions = [newSession, ...filteredSessions];
      
      // Keep only the most recent MAX_STORED_USERS
      const trimmedSessions = updatedSessions.slice(0, MAX_STORED_USERS);
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedSessions));
    } catch (error) {
      console.error('Failed to save user session:', error);
    }
  }

  /**
   * Remove a specific user session
   */
  static removeSession(username: string): void {
    try {
      const sessions = this.getSessions();
      const filteredSessions = sessions.filter(s => s.username !== username);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredSessions));
    } catch (error) {
      console.error('Failed to remove user session:', error);
    }
  }

  /**
   * Clear all stored sessions
   */
  static clearAllSessions(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear user sessions:', error);
    }
  }

  /**
   * Get a specific session by username
   */
  static getSession(username: string): StoredUserSession | null {
    const sessions = this.getSessions();
    return sessions.find(s => s.username === username) || null;
  }

  /**
   * Get recently logged in users (excluding current user)
   */
  static getRecentSessions(excludeUsername?: string): StoredUserSession[] {
    const sessions = this.getSessions();
    if (!excludeUsername) return sessions;
    return sessions.filter(s => s.username !== excludeUsername);
  }

  /**
   * Get the current OIDC user from localStorage
   */
  static getCurrentOidcUser(): any {
    try {
      const oidcData = localStorage.getItem(getActiveOidcStorageKey());
      return oidcData ? JSON.parse(oidcData) : null;
    } catch (error) {
      console.error('Failed to get current OIDC user:', error);
      return null;
    }
  }

  /**
   * Switch to a different user session by restoring their OIDC data
   */
  static switchToSession(username: string): boolean {
    try {
      console.log('[UserSessionStorage] Starting switch to:', username);
      
      const session = this.getSession(username);
      if (!session || !session.oidcUser) {
        console.error('[UserSessionStorage] Session not found or missing OIDC data for:', username);
        return false;
      }

      console.log('[UserSessionStorage] Found session:', {
        username: session.username,
        hasAccessToken: !!session.oidcUser.access_token,
        tokenLength: session.oidcUser.access_token?.length || 0,
        expiresAt: new Date(session.oidcUser.expires_at * 1000).toISOString()
      });

      // Get current OIDC user before switching
      const oidcStorageKey = getActiveOidcStorageKey();
      const currentOidcData = localStorage.getItem(oidcStorageKey);
      const currentUser = currentOidcData ? JSON.parse(currentOidcData) : null;
      console.log('[UserSessionStorage] Current OIDC user:', currentUser?.profile?.preferred_username);
      console.log('[UserSessionStorage] Environment OIDC config:', {
        ...getConfiguredOidcClientConfig(),
        computedStorageKey: oidcStorageKey,
      });

      // Update the lastLoginAt timestamp
      session.lastLoginAt = new Date().toISOString();
      this.saveSession(session);

      // Clear any stale OIDC state that might cause "No matching state" errors
      // These are temporary keys used during the OAuth flow
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('oidc.') && key !== oidcStorageKey) {
          keysToRemove.push(key);
        }
      }
      console.log('[UserSessionStorage] Removing stale OIDC keys:', keysToRemove.length);
      keysToRemove.forEach(key => {
        console.log('[UserSessionStorage]   - Removing:', key);
        localStorage.removeItem(key);
      });

      // Restore the OIDC user data to make the switch
      console.log('[UserSessionStorage] Setting OIDC user to:', session.username);
      localStorage.setItem(oidcStorageKey, JSON.stringify(session.oidcUser));
      
      // Verify it was set correctly
      const verifyOidcData = localStorage.getItem(oidcStorageKey);
      const verifyUser = verifyOidcData ? JSON.parse(verifyOidcData) : null;
      console.log('[UserSessionStorage] Verified OIDC user after switch:', verifyUser?.profile?.preferred_username);
      
      if (verifyUser?.profile?.preferred_username !== username) {
        console.error('[UserSessionStorage] VERIFICATION FAILED! Expected:', username, 'Got:', verifyUser?.profile?.preferred_username);
        return false;
      }
      
      console.log('[UserSessionStorage] ✅ Successfully switched to user:', username);
      return true;
    } catch (error) {
      console.error('[UserSessionStorage] Failed to switch session:', error);
      return false;
    }
  }

  /**
   * Check if a stored session's token is still valid (not expired)
   */
  static isSessionValid(username: string): boolean {
    try {
      const session = this.getSession(username);
      if (!session || !session.oidcUser) {
        return false;
      }

      // Check if token is expired
      const expiresAt = session.oidcUser.expires_at;
      if (!expiresAt) {
        return false;
      }

      // Add a 60 second buffer for clock skew
      const now = Math.floor(Date.now() / 1000);
      return expiresAt > (now + 60);
    } catch (error) {
      console.error('Failed to check session validity:', error);
      return false;
    }
  }
}
